import type {
  ContentBlock, IssueDetail, NewsletterSection, SectionArticle, SectionType,
} from "./segment-types";

export function newSectionId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const SECTION_LABEL: Record<SectionType, string> = {
  category: "Category heading",
  featured: "Featured link",
  doctor: "Doctor recommendation",
  footer: "Footer",
  articles: "Link cards",
  block: "Content block",
  banner: "Banner",
  tip: "Health tip",
  text: "Text",
  image: "Photo",
  cta: "Call to action",
  divider: "Divider",
  spacer: "Spacer",
};

/** The library, grouped the way an editor thinks rather than by data type. */
export const SECTION_GROUPS: {
  name: string;
  items: { type: SectionType; label: string; hint: string; layout?: NewsletterSection["layout"] }[];
}[] = [
  {
    name: "Structure",
    items: [
      { type: "category", label: "Category heading", hint: "The category this issue is about" },
      { type: "footer", label: "Footer", hint: "Logo, social links, unsubscribe" },
    ],
  },
  {
    name: "Featured",
    items: [{ type: "featured", label: "Featured article", hint: "One lead story, large" }],
  },
  {
    name: "Links",
    items: [
      { type: "articles", label: "Link cards", hint: "Articles and terms you choose, each with its own description" },
      { type: "articles", label: "Link cards, compact", hint: "The same, for short titles", layout: "grid" },
    ],
  },
  {
    name: "Content",
    items: [
      { type: "block", label: "Content block", hint: "Picture, heading, description and link — five layouts" },
      { type: "tip", label: "Health tip", hint: "Short practical advice" },
      { type: "doctor", label: "Doctor recommendation", hint: "A note from a clinician" },
      { type: "text", label: "Text", hint: "A paragraph" },
      { type: "image", label: "Photo", hint: "A picture" },
    ],
  },
  {
    name: "Marketing",
    items: [
      { type: "banner", label: "Banner", hint: "Full-width band" },
      { type: "cta", label: "Call to action", hint: "A button" },
    ],
  },
  {
    name: "Layout",
    items: [
      { type: "divider", label: "Divider", hint: "A rule between sections" },
      { type: "spacer", label: "Spacer", hint: "Vertical space" },
    ],
  },
];

/** Sections that draw their articles from a category. */
export function isArticleSection(type: SectionType): boolean {
  return type === "featured" || type === "articles";
}

export function emptySection(type: SectionType, layout?: NewsletterSection["layout"]): NewsletterSection {
  const base: NewsletterSection = { id: newSectionId(), type };
  if (type === "category") return { ...base, heading: "", category: null };
  if (type === "footer") return { ...base };
  if (type === "doctor") return { ...base, heading: "توصية من الطبي", text: "", url: "", cta_label: "اعرف المزيد" };
  if (type === "featured") return { ...base, heading: "", articles: [], category: null, count: 0 };
  if (type === "articles") {
    return {
      ...base,
      heading: layout === "grid" ? "المزيد من المقالات" : "مقالات ذات صلة",
      // count 0 means "only the links I add". Filling slots from the reader's
      // own category is a choice made in settings, not what happens by
      // default to a section someone just added.
      articles: [], category: null, count: 0, layout: layout ?? "list",
    };
  }
  if (type === "block") {
    return { ...base, layout: layout ?? "stacked", heading: "", text: "", image: "", url: "", cta_label: "اعرف المزيد" };
  }
  if (type === "banner") return { ...base, heading: "", text: "", url: "", cta_label: "اعرف المزيد" };
  if (type === "tip") return { ...base, heading: "نصيحة صحية", text: "" };
  if (type === "cta") return { ...base, cta_label: "اعرف المزيد", url: "" };
  return { ...base, text: "" };
}

/**
 * The shape a category-driven newsletter starts in.
 *
 * Starting from an empty canvas asks the editor to reinvent the format every
 * time, and the format is the product decision — category, one lead story,
 * related reading, a break, then more of the same category.
 */
export function defaultSections(): NewsletterSection[] {
  return [
    { id: newSectionId(), type: "category", heading: "", category: null },
    { id: newSectionId(), type: "featured", heading: "", articles: [], category: null, count: 0 },
    // An empty list of cards to add links to, rather than three slots that
    // fill themselves.
    { id: newSectionId(), type: "articles", heading: "مقالات ذات صلة", articles: [], category: null, count: 0, layout: "list" },
    { id: newSectionId(), type: "banner", heading: "", text: "", url: "", cta_label: "اعرف المزيد" },
    { id: newSectionId(), type: "footer" },
  ];
}

/** Older drafts were built from generic blocks; carry them over rather than lose them. */
export function sectionsFromBlocks(blocks: ContentBlock[]): NewsletterSection[] {
  return blocks.map((b): NewsletterSection => {
    const id = b.id || newSectionId();
    if (b.type === "article" || b.type === "link") {
      return {
        id, type: "articles", heading: "", category: null, layout: "list",
        articles: b.url ? [{ id, title: b.text || b.url, url: b.url }] : [],
      };
    }
    if (b.type === "hero") {
      return {
        id, type: "featured", heading: "", category: null,
        articles: [{ id, title: b.text || "", url: b.url || "", image: b.url }],
      };
    }
    if (b.type === "image") return { id, type: "image", image: b.url, text: b.alt };
    if (b.type === "cta") return { id, type: "cta", cta_label: b.text, url: b.url };
    if (b.type === "tip") return { id, type: "tip", heading: "نصيحة صحية", text: b.text };
    if (b.type === "divider") return { id, type: "divider" };
    if (b.type === "footer") return { id, type: "footer", text: b.text };
    if (b.type === "heading") return { id, type: "category", heading: b.text, category: null };
    return { id, type: "text", text: b.text };
  });
}

export function issueSections(issue: IssueDetail): NewsletterSection[] {
  if (issue.sections?.length) return issue.sections;
  if (issue.blocks?.length) return sectionsFromBlocks(issue.blocks);
  return [];
}

/** How many articles a section will show a recipient. */
export function sectionSlots(section: NewsletterSection): number {
  // No count means no automatic filling: a section shows the links added to
  // it and nothing else. Featured holds one, so its count is capped there.
  const want = section.count ?? 0;
  return section.type === "featured" ? Math.min(want, 1) : want;
}


export function articleFromHit(hit: {
  title: string; url?: string; category?: string; kind?: string;
  image?: string; read_minutes?: number; excerpt?: string;
}): SectionArticle {
  return {
    id: newSectionId(),
    title: hit.title,
    url: hit.url ?? "",
    category: hit.category,
    image: hit.image,
    read_minutes: hit.read_minutes,
    excerpt: hit.excerpt,
  };
}
